import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RetryApiAlert } from '@hevoio/hevo-ui-toolkit';

/**
 * The RUM agent, loaded by the app shell. Absent in tests and in any build without the script, so
 * every call is guarded rather than assumed.
 *
 * Typed with the call signature this file actually uses — a string label plus a details bag —
 * rather than a bare `Function`, which types neither the arguments nor the arity.
 */
type CoralogixRum = {
  error: (label: string, details: Record<string, unknown>) => void;
};

const coralogix = () => (window as unknown as { CoralogixRum?: CoralogixRum }).CoralogixRum;

export interface DashboardErrorBoundaryProps {
  /** Names the failing surface in the console line and the RUM record — `health`, `activate`, … */
  surface: string;
  /** Fallback copy. Written per call site, because "the rest of the page is fine" is not always true. */
  title: string;
  subTitle: string;
  dataId: string;
  /**
   * Changing this clears a caught error. Activate passes the route path, so navigating away from a
   * broken page recovers without a reload. Omit it where there is nothing to key recovery on.
   */
  resetKey?: string | number;
  /**
   * Coralogix event key, e.g. `pipeline-dashboard.render-error`. Present means "report this to RUM
   * under that key, so an alert can be keyed on it"; absent means console only.
   */
  reportKey?: string;
  /** Applied to the fallback alert itself. */
  className?: string;
  /** Wraps the fallback when set — for call sites that have to position it. No wrapper otherwise. */
  containerClassName?: string;
  /** Overrides the toolkit's default "Try Again" retry label. */
  retryLabel?: string;
  /**
   * Called whenever the boundary clears its error — the reader's retry, or a `resetKey` change.
   *
   * Remounting the children is not always enough to recover, because the state that decides what
   * they render lives OUTSIDE the boundary. The job drawer is the case: its `open` is the page's
   * `drawerOpen`, and the close button that would clear it belongs to the `Drawer` the fallback
   * replaced — so the flag stays true, retry remounts a drawer that throws again, and the page is
   * stuck until the tab is left. This is the owner's chance to put that state back.
   */
  onReset?: () => void;
  children: ReactNode;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains a render error to one surface, and reports it. `App.tsx` also mounts one around all the
 * routes as the last line of defence, so a route that fails to load or render shows a "Reload"
 * fallback instead of a blank page.
 *
 * Two problems this answers. A throw unmounts everything above it, so one bad payload took a whole
 * route or tab with it — the reader lost the working panels too, and the product looked broken
 * rather than one part of it. And nothing told us: components derive their output from shapes the
 * backend can change without notice, so an unanticipated one fails silently in production until
 * somebody screenshots it.
 *
 * A class, because `getDerivedStateFromError` has no hook equivalent. The toolkit's own
 * `ErrorBoundary` is not an alternative — it accepts neither props nor children.
 */
export class DashboardErrorBoundary extends Component<DashboardErrorBoundaryProps, DashboardErrorBoundaryState> {
  constructor(props: DashboardErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): DashboardErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: DashboardErrorBoundaryProps) {
    const { hasError } = this.state;
    const { resetKey } = this.props;

    if (hasError && prevProps.resetKey !== resetKey) {
      this.reset();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { surface, reportKey } = this.props;

    if (reportKey) {
      coralogix()?.error(reportKey, {
        surface,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }

    // eslint-disable-next-line no-console
    console.error(`[${surface}] contained render error:`, error, errorInfo.componentStack);
  }

  reset = () => {
    const { onReset } = this.props;

    this.setState({ hasError: false });
    // Both updates are raised inside one event handler, so React batches them into a single
    // commit: the owner's state and the cleared error land together, with no frame showing a
    // recovered boundary still driven by the stale flag. The call order is not what buys that.
    onReset?.();
  };

  render() {
    const { hasError } = this.state;
    const { children, title, subTitle, dataId, className, containerClassName, retryLabel } = this.props;

    if (!hasError) {
      return children;
    }

    /*
      Retry remounts the children, which restarts whatever they fetch — so the next payload gets a
      chance rather than the reader having to reload the page. Worth offering even though a render
      error is deterministic in the data that caused it: polled data means the retry runs against a
      newer payload, not the one that failed.
    */
    const fallback = (
      <RetryApiAlert
        title={title}
        subTitle={subTitle}
        secondaryCTAProps={retryLabel ? { children: retryLabel, onClick: this.reset } : { onClick: this.reset }}
        className={className}
        dataId={dataId}
      />
    );

    return containerClassName ? <div className={containerClassName}>{fallback}</div> : fallback;
  }
}
